
import { Vue, Component, Prop, Emit } from "vue-property-decorator"
import api from "../../honcierge-api"
import Base64Uploader from "@/components/Base64Uploader.vue"
import DeleteButtonWithConfirm from "@/components/DeleteButtonWithConfirm.vue"
import Articles from "@/components/SpecialFeatures/Articles.vue"
import { ArticleSearchMaster, SpecialFeature } from "types/index"
import { AxiosError } from "axios"

@Component({
  components: { Base64Uploader, DeleteButtonWithConfirm, Articles },
})
export default class EditDialog extends Vue {
  @Prop({ default: null })
  private id!: number | null
  @Prop()
  private articleSearchMaster!: ArticleSearchMaster["searchMaster"]
  @Prop({ default: 1 })
  private page!: number

  private title = ""
  private urlKey = ""
  private overview = ""
  private priority = 1
  private showOnTop = true
  private showTitle = false
  private isEnabled = true
  private banner: { content: string; filename: string } = {
    content: "",
    filename: "",
  }
  private articles: SpecialFeature["data"]["articles"] = []

  private dialog = false

  // rules for form
  private titleRules = [(title: string) => !!title || "タイトルは必須です"]
  private urlKeyRules = [(urlKey: string) => !!urlKey || "URLキーは必須です"]
  private overviewRules = [
    (overview: string) => !!overview || "概要文は必須です",
  ]
  private priorityRules = [
    (priority: number) => !!priority || "優先度は必須です",
  ]

  public createOrUpdateSpecialFeature(): void {
    if (!this.validate()) {
      return
    }
    if (this.id !== null) {
      api
        .updateSpecialFeature({
          id: this.id,
          title: this.title,
          urlKey: this.urlKey,
          overview: this.overview,
          priority: this.priority,
          showOnTop: this.showOnTop,
          showTitle: this.showTitle,
          isEnabled: this.isEnabled,
          banner: {
            filename: this.banner.filename,
            content: this.banner.content,
          },
          articleIds: this.articles.map((article) => article.id),
        })
        .then(() => {
          this.emitUpdateSpecialFeature(this.page)
        })
        .catch((e: AxiosError) => {
          if (e.response) {
            e.response.data.messages.forEach((messsage: string) => {
              this.$toasted.error(messsage)
            })
          }
        })
    } else {
      if (this.banner && (!this.banner.filename || !this.banner.content)) {
        this.$toasted.error("バナーを指定してください")
        return
      }
      api
        .createSpecialFeature({
          title: this.title,
          urlKey: this.urlKey,
          overview: this.overview,
          priority: this.priority,
          showOnTop: this.showOnTop,
          showTitle: this.showTitle,
          isEnabled: this.isEnabled,
          banner: {
            filename: this.banner.filename,
            content: this.banner.content,
          },
          articleIds: this.articles.map((article) => article.id),
        })
        .then(() => {
          this.emitCreateSpecialFeature()
        })
        .catch((e: AxiosError) => {
          if (e.response) {
            e.response.data.messages.forEach((message: string) => {
              this.$toasted.error(message)
            })
          }
        })
    }
  }

  public deleteSpecialFeature(): void {
    if (this.id != null) {
      api
        .deleteSpecialFeature(this.id)
        .then(() => {
          this.emitDeleteSpecialFeature(this.page)
        })
        .catch((error: AxiosError) => {
          console.log(error.stack)
          this.$toasted.error("特集の削除に失敗しました")
        })
    }
  }

  private setArticles(articles: SpecialFeature["data"]["articles"]) {
    this.articles = this.articles.concat(articles)
  }

  private removeArticleId(id: number) {
    const index = this.articles.findIndex((article) => article.id === id)
    this.articles.splice(index, 1)
  }

  private clear() {
    this.init()
    this.resetValidation()
  }

  private close() {
    this.dialog = false
    this.clear()
  }

  @Emit("emitCreateSpecialFeature")
  private emitCreateSpecialFeature() {
    this.close()
  }
  @Emit("emitUpdateSpecialFeature")
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private emitUpdateSpecialFeature(page: number) {
    this.close()
  }
  @Emit("emitDeleteSpecialFeature")
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private emitDeleteSpecialFeature(page: number) {
    this.close()
  }

  private init() {
    this.title = ""
    this.urlKey = ""
    this.overview = ""
    this.priority = 1
    this.showOnTop = true
    this.isEnabled = true
    this.showTitle = false
    this.banner = { content: "", filename: "" }
    const base64Uploader = this.$refs.base64Uploader as InstanceType<
      typeof Base64Uploader
    >
    base64Uploader.filename = ""
    this.articles = []
  }

  private loadImage(image: string, filename: string) {
    this.banner.content = image
    this.banner.filename = filename
  }

  public async open() {
    try {
      if (this.id !== null) {
        const response = await api.getSpecialFeature(this.id)
        const specialFeature = response.data.specialFeature

        this.title = specialFeature.title
        this.urlKey = specialFeature.urlKey
        this.overview = specialFeature.overview
        this.priority = specialFeature.priority
        this.showOnTop = specialFeature.showOnTop
        this.showTitle = specialFeature.showTitle
        this.isEnabled = specialFeature.isEnabled
        this.articles = specialFeature.articles
      } else {
        try {
          const response = await api.getMaxPriorityForSpecialFeatures()
          const maxPriority = response.data.maxPriority
          this.priority = maxPriority + 10
        } catch {
          console.log("特集の最大優先度の取得に失敗")
        }
      }
      this.dialog = true
    } catch {
      this.$toasted.error("特集の取得に失敗しました")
    }
  }

  private resetValidation(): void {
    const form = this.$refs.form as InstanceType<typeof EditDialog>
    return form.resetValidation()
  }

  private validate(): boolean {
    const form = this.$refs.form as InstanceType<typeof EditDialog>
    return form.validate()
  }
}
